import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchBusinessUnits,
  fetchServices,
  fetchTags,
  sohranitTekushchiyFilial,
  type BusinessUnit,
  type BusinessUnitType,
} from './api';
import { useFilialStore } from './store';

export function useBusinessUnits(filters: { type?: BusinessUnitType; withRelations?: string } = {}) {
  return useQuery({
    queryKey: ['business-units', filters],
    queryFn: () => fetchBusinessUnits(filters),
  });
}

export function useFilials() {
  return useBusinessUnits({ type: 'filial' });
}

export function useNetworks() {
  return useBusinessUnits({ type: 'network' });
}

export function useServices(filialId?: number) {
  return useQuery({
    queryKey: ['services', filialId ?? null],
    queryFn: () => fetchServices(filialId),
  });
}

export function useTags(category?: string) {
  return useQuery({
    queryKey: ['tags', category ?? null],
    queryFn: () => fetchTags(category),
  });
}

export function useCurrentFilialId(): number | null {
  return useFilialStore((s) => s.currentFilialId);
}

/**
 * ⛔⛔ ФИЛИАЛ ЭКРАНА — ТОЛЬКО ИЗ ШАПКИ (владелец, 24.09.2026, `s635` Дополнение 2:
 * «дублируется филиал блять какого лешего, уже была такая ошибка ранее в другом
 * разделе!!!»; op-rules §31.8). Своих переключателей филиала у экранов нет:
 * экран берёт ровно то, что показывает переключатель в шапке, — адрес
 * (`?filial_id`), а без него — сохранённый выбор. Тот же ответ, что рисует
 * `FilialPicker`: одно выражение на шапку и экраны. `null` — «Все филиалы».
 */
export function useFilialShapki(): number | null {
  const currentId = useCurrentFilialId();
  const [params] = useSearchParams();
  const izAdresa = params.get('filial_id');
  const adresnyy = izAdresa !== null && izAdresa !== '' ? Number(izAdresa) : null;

  return adresnyy !== null && Number.isFinite(adresnyy) && adresnyy > 0 ? adresnyy : currentId;
}

/*
 * ⛔ КЛЮЧ `zapis` ДОБАВЛЕН (`s311`, жалоба владельца 09.09.2026: «при клике на
 * филиал ничего не происходит»). Переключатель уехал в меню и стал общим для
 * всех разделов — но список сброса помнил только те экраны, ради которых его
 * когда-то заводили. Журнал дня, карточка записи и списки записи оставались от
 * прежнего филиала.
 */
/*
 * ⛔ КЛЮЧ `procedury` ДОБАВЛЕН (`s320`, Д47): экран «Процедуры» держал свой
 * выбор филиала, и при смене верхнего переключателя его список оставался от
 * прежнего филиала — ровно тот же класс, что чинил `s311` для `zapis`.
 */
const FILIAL_INVALIDATE_KEYS = ['sites', 'forms', 'customers', 'services', 'popups', 'zapis', 'procedury'];

export function useSetCurrentFilial() {
  const setStore = useFilialStore((s) => s.setCurrentFilial);
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useCallback(
    (id: number | null) => {
      setStore(id);
      /*
       * ⛔ `s320` Д3/Д47: ВЫБОР УЕЗЖАЕТ НА СЕРВЕР, А НЕ ТОЛЬКО В БРАУЗЕР.
       * Пункты меню — голые пути без `?filial_id`, и после любого перехода
       * сервер подставлял свой филиал: пикер говорил «Лэш СПб», журнал рисовал
       * Москву (живое репро владельца 15.09.2026). Сервер обязан знать выбор.
       * Отказ двери не роняет экран: он уже переключился, филиал не сохранится
       * до следующего входа — это меньшее зло, чем застрявший экран.
       */
      void sohranitTekushchiyFilial(id).catch(() => undefined);
      /*
       * ⛔ АДРЕС МЕНЯЕТСЯ РОУТЕРОМ, А НЕ `history.replaceState` (`s311`).
       * Прямая подмена адреса проходит МИМО роутера: `useSearchParams` о ней не
       * узнаёт, экран не перерисовывается — владелец кликал по филиалу, и не
       * происходило ровно ничего. Пока переключатель жил спрятанным, дефект был
       * незаметен; в меню он стал виден с первого клика.
       */
      const url = new URL(window.location.href);
      if (id === null) {
        url.searchParams.delete('filial_id');
      } else {
        url.searchParams.set('filial_id', String(id));
      }
      navigate(`${url.pathname}${url.search}`, { replace: true });
      for (const key of FILIAL_INVALIDATE_KEYS) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
    [setStore, qc, navigate]
  );
}

export function groupFilialsByNetwork(
  filials: BusinessUnit[] | undefined,
  networks: BusinessUnit[] | undefined
): { network: BusinessUnit | null; filials: BusinessUnit[] }[] {
  if (!filials) return [];
  const networkById = new Map<number, BusinessUnit>();
  (networks ?? []).forEach((n) => networkById.set(n.id, n));
  const groups = new Map<number | null, BusinessUnit[]>();
  for (const f of filials) {
    const key = f.parent_id ?? null;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(f);
  }
  return Array.from(groups.entries()).map(([parentId, list]) => ({
    network: parentId !== null ? (networkById.get(parentId) ?? null) : null,
    filials: list,
  }));
}
